import { type AiTagId, type HistoricalEventResponse } from '@solinkcorp/api-types';
import { type Immutable } from 'immer';
import { ImmutableModelStore } from 'solox';
import {
  type DebugEventType,
  ServiceDisplayDebuggerClass,
} from './components/ServiceDisplayDebugger/ServiceDisplayDebuggerClass';
import {
  type EventProcessingResult,
  isLineEvent,
  isZoneEvent,
  safeValidateSdmEvent,
  type ZoneEvent,
} from './data-structures/sdmEventTypes';
import { MAX_RETENTION_DURATION_REAL_TIME_WIDGETS } from './layout/getHorizonWindow';
import {
  type EventPair,
  type SdmEvent,
  type ServiceDisplayEventId,
  type ServiceDisplayEventState,
} from './types';

const DEFAULT_WINDOW_RANGE_MS = 60 * 60 * 1000;

export class ServiceDisplayEventsController {
  public state = new ImmutableModelStore<ServiceDisplayEventState>({
    rawEvents: new Map(), // used to prune data periodically from aiTagSummaryMap
    aiTagSummaryMap: new Map(), // tracks all relevant data relative to an aiTagId
    windowRangeMs: DEFAULT_WINDOW_RANGE_MS, // defaults to 1 hour; can be extended for longer-horizon widgets
  });

  /**
   * AI tag id → AI tag name. Used to set the `label` on each summary entry so the debugger
   * shows the AI tag's human-readable name rather than whichever zone/line label happened to
   * arrive first for that tag (which was misleading when zones and lines shared a tag).
   * Populated by `setAiTagNames` from the consumer; not part of `state` because it's purely
   * an input — labels are still stored on the per-tag summary so React re-renders work.
   */
  private aiTagNamesById: ReadonlyMap<AiTagId, string> = new Map();

  /**
   * When true (default), zone exits assume FIFO order and clear earlier orphaned entries.
   * When false (devices with reliable pairing / `clientSettings.maxOccupancyEnabled`), only
   * events older than the retention window are dropped. Set from React via
   * `setAssumeFifoEventsOrder`.
   */
  private assumeFifoEventsOrder = true;

  /**
   * Adjust the event retention window. The window only ever grows: once a layout
   * with a longer horizon (e.g. current day) has been seen, we keep events for at
   * least that long so widgets always have enough data. Defaults to 1h on init.
   */
  setWindowRangeMs(ms: number) {
    const target = Math.max(DEFAULT_WINDOW_RANGE_MS, this.state.current.windowRangeMs, ms);
    if (this.state.current.windowRangeMs === target) return;
    this.state.update((state) => {
      state.windowRangeMs = target;
    });
  }

  /**
   * Configure whether zone-exit pruning assumes FIFO event order (true, legacy orphan-clearing)
   * or keeps concurrent occupancy until the retention window (false). Call from a React effect
   * whenever `clientSettings.maxOccupancyEnabled` is available — pass the inverse of that flag.
   */
  setAssumeFifoEventsOrder(assumeFifo: boolean) {
    this.assumeFifoEventsOrder = assumeFifo;
  }

  /**
   * Provide the AI tag id → name mapping that should be used as the display label on each
   * summary entry. Call this whenever the consumer's AI tag list loads or changes; existing
   * summary entries are updated in place. Unknown tags fall back to the raw aiTagId.
   */
  setAiTagNames(namesById: ReadonlyMap<AiTagId, string>) {
    this.aiTagNamesById = new Map(namesById);
    this.state.update((state) => {
      for (const [aiTagId, summary] of state.aiTagSummaryMap) {
        const nextLabel = this.aiTagNamesById.get(aiTagId) ?? aiTagId;
        if (summary.label !== nextLabel) {
          summary.label = nextLabel;
        }
      }
    });
  }

  private debugger = new ServiceDisplayDebuggerClass();

  debugLog(type: DebugEventType, message: string, data?: any) {
    this.debugger.log(type, message, data);
  }

  registerDebuggerCallbacks(
    addEvent: (event: {
      type: DebugEventType;
      message: string;
      data?: any;
      formattedData?: string;
    }) => void,
    updateSummary: (summaryMap: Map<string, any>) => void,
  ) {
    this.debugger.registerCallbacks(addEvent, updateSummary);
  }

  unregisterDebuggerCallbacks() {
    this.debugger.unregisterCallbacks();
  }

  /**
   * Pushes a snapshot of the summary map to the debugger. Must be called *after* the relevant
   * `state.update(...)` has committed so it reads the finalized `this.state.current`: reading a
   * live immer draft here would (a) lag one update behind (the shallow `new Map` copies the
   * pre-commit committed state) and (b) leak revoked draft proxies once the update finishes,
   * crashing the debugger on later reads with "Cannot perform 'get' on a proxy that has been
   * revoked".
   */
  updateDebuggerSummary() {
    // Convert ReadonlyMap to Map for the debugger
    const summaryMap = new Map(this.state.current.aiTagSummaryMap);
    this.debugger.updateSummary(summaryMap);
  }

  /**
   * Clears all event data from the controller.
   * This should be called when switching devices to prevent data contamination.
   */
  clearAllEvents(): void {
    this.debugLog('clear', 'Clearing all event data');
    this.state.update((state) => {
      state.rawEvents.clear();
      state.aiTagSummaryMap.clear();
    });
    this.updateDebuggerSummary();
  }

  /**
   * Processes historical events data by sorting and processing each event.
   * This is typically called once when historical data is first loaded.
   */
  processHistoricalEvents(historicalData: HistoricalEventResponse[]): void {
    if (!historicalData || historicalData.length === 0) {
      this.debugLog('historical_data', 'No historical data to process');
      return;
    }

    this.debugLog('historical_data', `Processing ${historicalData.length} historical events`);

    // Sort events by timestamp (oldest first) to maintain chronological order
    const sortedEvents = historicalData.sort((a, b) => a.time_ms - b.time_ms);

    // Process each historical event
    sortedEvents.forEach((obj) => {
      try {
        const eventData = JSON.parse(obj.event_json);
        this.processEvent(eventData);
      } catch (error) {
        this.debugLog('historical_data_error', 'Failed to parse historical event', {
          error,
          rawEvent: obj.event_json,
        });
      }
    });

    this.debugLog('historical_data', 'Finished processing historical events');
  }

  processEvent(rawEvent: unknown): EventProcessingResult {
    try {
      // Validate the incoming event
      const validationResult = safeValidateSdmEvent(rawEvent);
      if (!validationResult.success) {
        this.debugLog('validation_error', 'Invalid event structure', {
          error: validationResult.error.issues,
          rawEvent,
        });
        return { success: false, error: 'Invalid event structure' };
      }

      const sdmEvent = validationResult.data;

      // Debug log incoming event
      this.debugLog(
        'onEvent',
        `Processing ${sdmEvent.evt_type} event #${sdmEvent.evt_id}`,
        sdmEvent,
      );

      // Build unique key and pull ai tags from event
      const { eventIds, aiTagIds } = this.ingestIncomingEvent(sdmEvent);

      if (!eventIds || aiTagIds.length === 0) {
        this.debugLog('onEvent', `⚠️ Skipped event - no AI tags or person detection`, sdmEvent);
        return { success: true, eventIds: [], aiTagIds: [] };
      }

      for (const eventId of eventIds) {
        this.updateSummaryMapping(eventId, aiTagIds, sdmEvent);
      }

      return { success: true, eventIds, aiTagIds };
    } catch (error) {
      this.debugLog('processing_error', 'Error processing event', { error, rawEvent });
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Processes an SDM event and returns the generated event IDs and AI tag IDs.
   * If the event has no AI tags assigned, returns undefined for eventIds.
   */
  ingestIncomingEvent(sdmEvent: SdmEvent): {
    eventIds: ServiceDisplayEventId[] | undefined;
    aiTagIds: AiTagId[];
  } {
    if (this.hasNoAiTagsAssignedOrIsPersonDetection(sdmEvent)) {
      return { eventIds: undefined, aiTagIds: [] };
    }

    const aiTagIds = sdmEvent.aiTagIds;
    const eventIds = this.storeIncomingEvent(sdmEvent, aiTagIds);

    return { eventIds, aiTagIds };
  }

  createUniqueKey(aiTagId: AiTagId, sdmEvent: SdmEvent): ServiceDisplayEventId {
    if (isZoneEvent(sdmEvent)) {
      return `${sdmEvent.entry_ms}:${aiTagId}:${sdmEvent.cam_id}:${sdmEvent.label.replace(
        /\s+/g,
        '_',
      )}:${sdmEvent.metadata.polygonId}`;
    } else if (isLineEvent(sdmEvent)) {
      return `${sdmEvent.start_ms}:${aiTagId}:${
        sdmEvent.cam_id
      }:${sdmEvent.event_line.label.replace(/\s+/g, '_')}`;
    }
    throw new Error(`Unsupported event type: ${(sdmEvent as any).evt_type}`);
  }

  /**
   * Prunes events outside the retention window from `rawEvents` to bound memory growth.
   *
   * Widget metrics no longer depend on precomputed counters maintained in `aiTagSummaryMap`,
   * so this no longer touches the summary map's "historical" fields (they don't exist anymore).
   * Counts/averages are derived on demand from `rawEvents` via `countEventsSince` /
   * `averageZoneTimeSecondsSince`, both of which take an explicit `sinceMs` and so are immune
   * to the retention window being wider than 1 hour.
   */
  pruneMap(timeNow: number) {
    const cutOffTime = timeNow - this.state.current.windowRangeMs;
    this.debugLog(
      'prune',
      `Scanning for events before: ${new Date(cutOffTime).toLocaleTimeString()}...`,
    );
    const rawEvents = this.state.current.rawEvents;
    this.state.update((state) => {
      // EventId format: `${timestamp}:${aiTagId}:${camId}:${label}`
      const sortedEvents = Array.from(rawEvents.entries());
      let isEarlyBreakIn = 10;

      for (let i = 0; i < sortedEvents.length; i++) {
        const [eventId, eventPair] = sortedEvents[i];

        /**
         * Early break: start countdown once we see consecutive events within cutOffTime.
         * note we are comparing Strings "124000000:aiTagId:camId:label" >= "123000000" which is a thing in JS
         **/
        if (
          eventId >= cutOffTime.toString() &&
          i + 1 < sortedEvents.length &&
          sortedEvents[i + 1][0] >= cutOffTime.toString()
        ) {
          isEarlyBreakIn--;
          if (isEarlyBreakIn === 0) {
            break;
          }
        }

        const startEvent = eventPair.start;
        const endEvent = eventPair.end;
        if (!startEvent) continue;

        if (startEvent.evt_type === 'zone') {
          const exitMs = (endEvent as ZoneEvent)?.exit_ms ?? null;

          // Completed occupancies whose exit is older than the window, OR orphaned in-progress
          // entries (exit never arrived) whose entry is older than the window. The latter would
          // otherwise linger forever, since in-progress zones have no exit time to prune against.
          const isCompletedAndExpired = exitMs !== null && exitMs < cutOffTime;
          const isInProgressOrphan = exitMs === null && startEvent.entry_ms < cutOffTime;
          if (isCompletedAndExpired || isInProgressOrphan) {
            startEvent.aiTagIds.forEach((aiTagId) => {
              this.debugLog(
                'prune',
                isInProgressOrphan
                  ? `Found orphaned zone events: #${startEvent?.evt_id}`
                  : `Found completed zone events: #${startEvent?.evt_id} & #${endEvent?.evt_id}. Removing...`,
                { eventId, cutOffDate: new Date(cutOffTime).toLocaleTimeString(), aiTagId },
              );
            });
            // A completed occupancy already had its live state cleared when its exit ran
            // adjustZoneSummary. An orphan never got an exit, so it still occupies
            // activeZoneEvents / totalVehiclesInZoneNow — clear it here or the widgets that
            // read those keep counting it forever.
            if (isInProgressOrphan) {
              this.removeFromActiveZone(state, eventId, startEvent.aiTagIds);
            }
            state.rawEvents.delete(eventId);
          }
        } else if (startEvent.evt_type === 'line') {
          const startMs = startEvent.start_ms;
          if (startMs < cutOffTime) {
            this.debugLog('prune', `Pruning event ID: ${startEvent.evt_id}`, {
              eventId,
              startMs,
              cutOffTime,
            });
            state.rawEvents.delete(eventId);
          }
        }
      }
    });
    // Push after commit so the debugger snapshots finalized state, not a revoked draft.
    this.updateDebuggerSummary();
  }

  /**
   * Updates the live "in zone now" state for an incoming event.
   * Historical counts/averages are derived from `rawEvents` on demand by widgets and the
   * debugger, so this only needs to track entries/exits to maintain `activeZoneEvents` and
   * `totalVehiclesInZoneNow`. Line events don't affect any live state — they're only stored
   * in `rawEvents`, so this is a no-op for them.
   */
  updateSummaryMapping(eventId: ServiceDisplayEventId, aiTagIds: AiTagId[], sdmEvent: SdmEvent) {
    if (!isZoneEvent(sdmEvent)) return;

    if (sdmEvent.drive_off === true) {
      this.state.update((state) => {
        this.adjustDriveOffCount(state, eventId, aiTagIds, sdmEvent.evt_id.toString());
      });
      // Push after commit so the debugger snapshots finalized state, not a revoked draft.
      this.updateDebuggerSummary();
      return;
    }

    this.state.update((state) => {
      this.adjustZoneSummary(
        state,
        eventId,
        aiTagIds,
        sdmEvent.entry_ms,
        sdmEvent.exit_ms,
        sdmEvent.evt_id.toString(),
      );
    });
    // Push after commit so the debugger snapshots finalized state, not a revoked draft.
    this.updateDebuggerSummary();
  }

  adjustZoneSummary(
    state: ServiceDisplayEventState,
    key: ServiceDisplayEventId,
    aiTagIds: AiTagId[],
    entryMs: number,
    exitMs: number | null,
    uid: string,
  ) {
    for (const aiTagId of aiTagIds) {
      const aiTagSummary = state.aiTagSummaryMap.get(aiTagId);

      if (!aiTagSummary) continue;

      const hasLeftZone = entryMs && exitMs;
      if (hasLeftZone) {
        aiTagSummary.totalVehiclesInZoneNow--;

        const updatedZoneEvents = this.getCurrentEventsInZone(
          key,
          aiTagSummary.activeZoneEvents,
          exitMs,
          uid,
          aiTagId,
        );

        aiTagSummary.activeZoneEvents = [...updatedZoneEvents];
      } else {
        if (aiTagSummary.activeZoneEvents.includes(key)) {
          continue;
        }
        aiTagSummary.totalVehiclesInZoneNow++;

        const currentZoneEvents = [...aiTagSummary.activeZoneEvents, key].sort();
        aiTagSummary.activeZoneEvents = currentZoneEvents as ServiceDisplayEventId[];

        const tsNow = Date.now();
        const zoneLabel = key.split(':')[3];
        const logMessage = `[RT_ZONE] - ${
          tsNow - entryMs
        }ms Entering zone ${zoneLabel} @ ${new Date(entryMs).toLocaleTimeString()}`;
        this.debugLog('RT_ZONE', logMessage, {
          aiTagId,
          uid,
          zoneLabel,
          entryMs,
          exitTime: null, //this is always null on entry
          currentZoneEvents,
          timeInZone: tsNow - entryMs,
        });
      }
    }
  }

  /**
   * Drops a live occupancy from `activeZoneEvents` and `totalVehiclesInZoneNow` for every
   * tag the event was counted under. Used both when an exit arrives (drive-off) and when
   * pruneMap ages out an orphaned in-progress entry whose exit never came.
   */
  removeFromActiveZone(
    state: ServiceDisplayEventState,
    eventId: ServiceDisplayEventId,
    aiTagIds: readonly AiTagId[],
  ) {
    for (const aiTagId of aiTagIds) {
      const aiTagSummary = state.aiTagSummaryMap.get(aiTagId);
      if (!aiTagSummary) continue;

      aiTagSummary.totalVehiclesInZoneNow = Math.max(0, aiTagSummary.totalVehiclesInZoneNow - 1);
      aiTagSummary.activeZoneEvents = aiTagSummary.activeZoneEvents.filter(
        (key) => key !== eventId,
      );
    }
  }

  adjustDriveOffCount(
    state: ServiceDisplayEventState,
    eventId: ServiceDisplayEventId,
    aiTagIds: readonly AiTagId[],
    uid: string,
  ) {
    this.removeFromActiveZone(state, eventId, aiTagIds);

    for (const aiTagId of aiTagIds) {
      this.debugLog('drive_off', `Drive-off detected`, { aiTagId, uid, eventId });
    }
  }

  hasNoAiTagsAssignedOrIsPersonDetection(sdmEvent: SdmEvent): boolean | undefined {
    if (sdmEvent.aiTagIds === undefined) return undefined;

    const isHumanDetection =
      (isLineEvent(sdmEvent) && sdmEvent.event_line.object_kinds.includes('person')) ||
      (isZoneEvent(sdmEvent) && sdmEvent.object_kind === 'person');

    const isTagless = sdmEvent.aiTagIds.length === 0;
    return isHumanDetection || isTagless;
  }

  // Creates unique key, stores event or throws error if duplicate unique key is generated
  private storeIncomingEvent(sdmEvent: SdmEvent, aiTagIds: AiTagId[]): ServiceDisplayEventId[] {
    const eventIds: ServiceDisplayEventId[] = [];

    this.state.update((state) => {
      for (const aiTagId of aiTagIds) {
        // First time here? Setup a summary mapping
        if (!state.aiTagSummaryMap.has(aiTagId)) {
          const label = sdmEvent.evt_type === 'zone' ? sdmEvent.label : sdmEvent.event_line?.label;
          this.createNewSummaryMapping(state, aiTagId, label);
        }

        // Create a unique key for the event
        const eventId = this.createUniqueKey(aiTagId, sdmEvent);

        /**
         * Check for duplicate events; important on page loads, as order isn't guaranteed on fetching
         * historical data, and we need to filter out duplicate events when updating our summary mappings.
         * Also need to make sure we have a clean list of EventPairs (used to track running historical averages)
         * */
        const isDuplicate = this.checkForDuplicateSaveEvent(state, eventId, sdmEvent);

        // To handle event loss on SSE reconnect, we fetch the last N seconds of data.
        // To prevent double counts, only push non duplicants
        if (!isDuplicate) eventIds.push(eventId);
      }
    });

    return eventIds;
  }

  // Updates EventPair object and catches duplicate events preventing double counting of events.
  private checkForDuplicateSaveEvent(
    state: ServiceDisplayEventState,
    eventId: ServiceDisplayEventId,
    sdmEvent: SdmEvent,
  ): boolean {
    const existingEventPair = state.rawEvents.get(eventId);

    if (!existingEventPair) {
      const eventPair: EventPair = { start: null, end: null };
      this.updateEventPair(eventPair, sdmEvent);
      state.rawEvents.set(eventId, eventPair);
      return false;
    }

    const isDuplicate =
      (isLineEvent(sdmEvent) &&
        existingEventPair.start &&
        this.isSameEvent(existingEventPair.start.evt_id, sdmEvent.evt_id)) ||
      (isZoneEvent(sdmEvent) &&
        ((existingEventPair.start &&
          this.isSameEvent(existingEventPair.start.evt_id, sdmEvent.evt_id)) ||
          (existingEventPair.end &&
            this.isSameEvent(existingEventPair.end.evt_id, sdmEvent.evt_id))));

    if (isDuplicate) {
      this.debugLog(
        'duplicate_detected',
        `👀 Duplicant detected! evt_type: ${sdmEvent.evt_type} evt_id: ${sdmEvent.evt_id}`,
        {
          evt_type: sdmEvent.evt_type,
          evt_id: sdmEvent.evt_id,
          sdmEvent,
        },
      );
      return true;
    }

    this.updateEventPair(existingEventPair, sdmEvent);
    return false;
  }

  private isSameEvent(uid1: number, uid2: number): boolean {
    return uid1 === uid2;
  }

  /**
   * Updates the EventPair type with the new event data. EventPairs are used to track the 'sliding window' of
   * historical data. It's mostly used for adjusting our aiTagSummaryMap values.
   *
   * Note: With the addition of drive offs, we update the 'eventPair.end' with the drive off event, replacing the exit event,
   * this is used in pruneMap to decrement both drive off events and exit events at the same time.
   * Not 100% accurate, but to add a new EventPair object to track drive offs would be overkill for a ~30s accuracy boost on a 1hr average value.
   *
   * @param eventPair - The event pair to update.
   * @param sdmEvent - The new event to update the event pair with.
   */
  private updateEventPair(eventPair: EventPair, sdmEvent: SdmEvent): void {
    if (isZoneEvent(sdmEvent)) {
      const isZoneEntry = sdmEvent.exit_ms === null;
      const isZoneExit = sdmEvent.exit_ms !== null;

      if (isZoneEntry) {
        eventPair.start = sdmEvent;
      } else if (isZoneExit) {
        eventPair.end = sdmEvent;
        if (!eventPair.start) {
          eventPair.start = { ...sdmEvent, exit_ms: null }; //little hack to ensure start object is not null when cleaning up zone events on initial page load
        }
      }
    } else if (isLineEvent(sdmEvent)) {
      eventPair.start = sdmEvent;
      eventPair.end = null;
    }
  }
  /**
   * Creates a new summary mapping for a new AI tag. This is used to display real-time metrics.
   * @param state - The current state of the service display events controller.
   * @param aiTagId - The ID of the AI tag to create a summary mapping for.
   * @param label - The label of the Zone/Line event, used as a fallback to help identify in debug mode when the events references tag ids that may not exist anymore
   * The debugger panel (Down Arrow + Right Arrow + Right Arrow + A) reads this label.
   */
  private createNewSummaryMapping(
    state: ServiceDisplayEventState,
    aiTagId: AiTagId,
    label?: string,
  ) {
    state.aiTagSummaryMap.set(aiTagId, {
      activeZoneEvents: [],
      totalVehiclesInZoneNow: 0,
      label: this.aiTagNamesById.get(aiTagId) ?? label ?? aiTagId,
    });
  }

  private getCurrentEventsInZone(
    key: ServiceDisplayEventId,
    currentZoneEvents: ServiceDisplayEventId[],
    exitMs: number,
    uid: string,
    aiTagId: string,
  ) {
    const indexToKeepFrom = this.getCurrentZoneEventsIndexToKeepFrom(currentZoneEvents, key);
    const orphanedZoneEvents =
      indexToKeepFrom >= 0
        ? currentZoneEvents.slice(0, indexToKeepFrom)
        : ([] as ServiceDisplayEventId[]);
    const updatedZoneEvents =
      indexToKeepFrom >= 0 ? currentZoneEvents.slice(indexToKeepFrom) : currentZoneEvents;
    const remainingZoneEvents = updatedZoneEvents.filter((zoneKey) => {
      return zoneKey !== key;
    });

    const ts = key.split(':')[0];
    const tsNow = Date.now();
    const zoneLabel = key.split(':')[3];
    const logMessage = `[RT_ZONE] - ${
      tsNow - Number(ts)
    }ms Vehicle left zone ${zoneLabel} @ ${new Date(Number(ts)).toLocaleTimeString()}`;

    this.debugLog('RT_ZONE', logMessage, {
      aiTagId,
      uid,
      zoneLabel,
      entryMs: Number(ts),
      exitTime: exitMs,
      remainingZoneEvents,
      orphanedZoneEvents,
      timeInZone: tsNow - Number(ts),
    });
    return remainingZoneEvents;
  }

  /**
   * Gets the index to keep from for the current zone events.
   * Behaviour depends on `this.assumeFifoEventsOrder` (inverse of `clientSettings.maxOccupancyEnabled`):
   * - If true, we limit the impact of orphaned zone events by having a First In, First Out assumption.
   *   This causes any exit event to clear all active events (entries with no exit counterpart) that were entered before the exit event.
   *   In other words, if a vehicle exits the zone, all the vehicles that have entered before it are removed from the active zone events.
   *   This skews the count towards lower values if the SDM client is connected to a zone tracking system following more than 1 lane.
   * - If false (device has a fix for orphaned zone events), we keep all active zone events that have not exited in the past 24h.
   *
   * @param currentZoneEvents - The current zone events.
   * @param key - The key of the event to keep.
   * @returns The index to keep from.
   */
  private getCurrentZoneEventsIndexToKeepFrom(
    currentZoneEvents: ServiceDisplayEventId[],
    key: ServiceDisplayEventId,
  ) {
    if (this.assumeFifoEventsOrder) {
      return currentZoneEvents.findIndex((zoneKey) => zoneKey === key);
    }

    const cutOffTime = Date.now() - MAX_RETENTION_DURATION_REAL_TIME_WIDGETS;
    const index = currentZoneEvents.findIndex((zoneKey) => zoneKey >= `${cutOffTime}`);
    // findIndex returns -1 when every entry is older than the cutoff. The caller treats -1 as
    // "keep the full list", which is only correct for the FIFO branch above (key not found).
    // Here it means "nothing is fresh enough to keep", so map it to length: keep-from-length
    // slices to an empty "keep" list and the full list becomes "orphaned".
    return index === -1 ? currentZoneEvents.length : index;
  }
}

/**
 * A read-only view of the controller's rawEvents map. Callers obtain `rawEvents` from
 * `useControllerState`, which returns the immer-immutable snapshot. The helpers below only
 * read from the map, so the immutable view is sufficient.
 */
type RawEventsView = ReadonlyMap<ServiceDisplayEventId, Immutable<EventPair>>;

export type CountEventKind = 'line' | 'zone' | 'driveOff';

/**
 * Pure aggregations over a `rawEvents` map. Exposed as top-level functions (not class members)
 * so widgets can pass the `rawEvents` value returned by `useControllerState` directly — no
 * hidden read from controller internals, and React memoization deps line up with the data
 * the function actually consumes.
 */

/**
 * Number of events for the given tags that overlap the time window [sinceMs, ∞). An event
 * overlaps the window when it starts within the window OR ends within the window — i.e. any
 * part of the event intersects [sinceMs, now]. This matches the product rule: "events that
 * start before but end inside the period are still counted".
 *
 * `kind` chooses what we're counting:
 *   - 'line':     line crossings (instantaneous at start_ms)
 *   - 'zone':     zone occupancy intervals (entry_ms..exit_ms, or in-progress with exit_ms=null)
 *   - 'driveOff': drive-off events (instantaneous at exit_ms when the trailing event has drive_off=true)
 *
 * The caller must ensure the underlying retention window is at least as long as `now - sinceMs`;
 * otherwise older events will have been pruned and won't be counted.
 */
export function countEventsSince(
  rawEvents: RawEventsView,
  tagIds: readonly AiTagId[],
  sinceMs: number,
  kind: CountEventKind,
): number {
  if (!tagIds.length) return 0;
  const tagSet = new Set(tagIds);
  let count = 0;
  for (const pair of rawEvents.values()) {
    const { end, start } = pair;
    if (!start) continue;
    if (!start.aiTagIds.some((tid) => tagSet.has(tid as AiTagId))) continue;

    if (kind === 'line' && start.evt_type === 'line' && start.start_ms >= sinceMs) {
      // Line events are instantaneous; include when they occurred at or after sinceMs.
      count++;
    } else if (kind === 'zone' && start.evt_type === 'zone') {
      // Include if the zone occupancy overlaps the window: in-progress (no end yet) or
      // ended at/after sinceMs. Events that ended before sinceMs are excluded.
      const exitMs = end?.evt_type === 'line' ? end.end_ms : (end?.exit_ms ?? null);
      if (end?.evt_type === 'zone' && end.drive_off === true) continue;
      if (exitMs == null || exitMs >= sinceMs) count++;
    } else if (kind === 'driveOff' && end?.evt_type === 'zone') {
      // driveOff: instantaneous at exit_ms of the trailing event.
      if (!end || end.drive_off !== true) continue;
      if (end.exit_ms != null && end.exit_ms >= sinceMs) count++;
    }
  }

  return count;
}

/**
 * Average duration (in seconds) of zone events for the given tags that overlap the time
 * window [sinceMs, ∞). Events whose occupancy ended before `sinceMs` are excluded; events
 * that started before but ended inside the window are included with their full duration.
 * Returns 0 when there are no qualifying completed events.
 */
export function averageZoneTimeSecondsSince(
  rawEvents: RawEventsView,
  tagIds: readonly AiTagId[],
  sinceMs: number,
): number {
  if (!tagIds.length) return 0;
  const tagSet = new Set(tagIds);
  let totalMs = 0;
  let count = 0;

  for (const pair of rawEvents.values()) {
    const start = pair.start;
    const { end } = pair;

    if (!start || !end) continue;
    if (start.evt_type !== 'zone' || end.evt_type !== 'zone') continue;
    if (!start.aiTagIds.some((tid) => tagSet.has(tid as AiTagId))) continue;
    // Drive-offs aren't a completed zone visit — exclude them from the average duration so
    // the rolling-average widget isn't skewed by abandoned events. Matches `countEventsSince`'s
    // zone branch which also filters drive-offs out.
    if (end.drive_off === true) continue;

    const exitMs = end.exit_ms;
    // Include the event if its occupancy overlaps [sinceMs, now]: exit must be at or after sinceMs.
    if (exitMs == null || exitMs < sinceMs) continue;
    totalMs += exitMs - start.entry_ms;
    count++;
  }

  if (count === 0) return 0;
  return totalMs / count / 1000;
}

/**
 * Average total journey time (in seconds) for the given tags over the window [sinceMs, ∞).
 *
 * Same window semantics as `averageZoneTimeSecondsSince`, with two differences:
 *  - Only events whose trailing zone event carries a non-null `vehicle_journey` qualify.
 *    The edge attaches that object solely on an end-zone exit it could pair with a preceding
 *    start-zone visit, so this naturally restricts the average to complete journeys.
 *  - The duration comes from `total_vehicle_time` (already the end-zone exit minus the
 *    start-zone entry, in ms) rather than from subtracting this event's own timestamps —
 *    the journey spans multiple zones, so `exit_ms - entry_ms` would only measure the last leg.
 *
 * Drive-offs are excluded and the window is gated on `end.exit_ms >= sinceMs`, matching the
 * zone-time average. Returns 0 when no event qualifies.
 */
export function averageJourneyTimeSecondsSince(
  rawEvents: RawEventsView,
  tagIds: readonly AiTagId[],
  sinceMs: number,
): number {
  if (!tagIds.length) return 0;
  const tagSet = new Set(tagIds);
  let totalMs = 0;
  let count = 0;

  for (const pair of rawEvents.values()) {
    const start = pair.start;
    const { end } = pair;

    if (!start || !end) continue;
    if (start.evt_type !== 'zone' || end.evt_type !== 'zone') continue;
    if (!start.aiTagIds.some((tid) => tagSet.has(tid as AiTagId))) continue;
    // A drive-off never completed the journey, so it would skew the average.
    if (end.drive_off === true) continue;

    // Both optional and nullable on the wire — anything but a populated journey means this
    // exit wasn't an end-zone exit the edge could pair with a start zone.
    const journey = end.vehicle_journey;
    if (!journey) continue;

    const exitMs = end.exit_ms;
    // Include the event if it overlaps [sinceMs, now]: exit must be at or after sinceMs.
    if (exitMs == null || exitMs < sinceMs) continue;

    totalMs += journey.total_vehicle_time;
    count++;
  }

  if (count === 0) return 0;
  return totalMs / count / 1000;
}
